/* eslint-disable linebreak-style */
import React, {
  ChangeEvent,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import _ from 'lodash';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Theme,
  Tooltip,
} from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Pagination } from '@material-ui/lab';
import { makeStyles } from '@material-ui/styles';

import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { ApiFilterCriteria, Repository } from '../types';
import { colors } from '../config/theme';
import ConditionalWrapper from './ConditionalWrapper';
import SearchContext from './search/SearchContext';
import AppContext from '../AppContext';
import RoleViewManager from '../RoleViewManager';
import { FilterColumnOption } from './filter-column/types';
import CheckboxFilter from './filter-column/CheckboxFilter';
import DateFilter from './filter-column/DateFilter';
import DateRangeFilter from './filter-column/DateRangeFilter';
import TextFilter from './filter-column/TextFilter';
import axios, { CancelTokenSource } from 'axios';

interface Item {
  id: string;
  [key: string]: any;
}

export interface ItemAction {
  key: string;
  element: JSX.Element;
}

export interface Filter {
  type: 'text' | 'checkbox' | 'datepicker' | 'dateRange';
  field: string;
  name: string;
  options?: FilterColumnOption[];
  infoText?: string;
}

export interface Column {
  // Name of the column as shown in the header.
  name: string;
  // Name of the field to show.
  field: string;
  // Determines if the user can sort on this column.
  sortable?: boolean;
  // Filters associated with the column.
  filter?: Filter | undefined;
  // Custom rendering function for this column.
  render?: (item: any, roleViewManager?: RoleViewManager) => ReactNode;
}

export const PlaceholderColumn: Column = {
  name: 'N/A',
  field: 'id',
  sortable: false,
  render: () => 'N/A',
};

interface DataTableProps {
  // A unique identifier for this data table.
  id: string;
  // The repository to use for retrieving the data.
  repository: Repository<unknown>;
  // All the columns of the data table.
  columns: Column[];
  // All filters of the data table.
  filters?: Filter[];
  // Whether items in the data table can be deleted or not.
  deletable?: boolean;
  // Callback when deleting an item fails.
  onDeleteFail?: (item: any) => void;
  // Callback that renders actions that are available for each item.
  actions?: (item: any) => ReactNode;
  // Callback when the data table loads data.
  onLoad?: (items: any, totalCount: number) => void;
  // Message when the user attempts to delete an item.
  deleteItemMessage?: (item: any) => string;
  // Custom class for the data table.
  className?: string;
  // Whether the data table is contained in a container or not.
  contained?: boolean;
  // Whether the filter state should be persisted.
  persistFilters?: boolean;
  // The filtering that's set on load.
  defaultFilters?: ApiFilterCriteria;
  // Whether the search should be triggered manually or not.
  triggerSearchManually?: boolean;
  // Callback when filters change.
  onFiltersChange?: (filters: ApiFilterCriteria) => void;
  // Callback that renders a table row.
  renderRow?: (
    columns: React.ReactElement,
    item: any,
    rowClassName: string,
    columnClassName: string,
  ) => React.ReactElement;
  // Callback that returns a style for a table row based on its item
  styleRow?: (item: any) => 'tr' | 'trHighlight';
  // Action that needs to be executed if a row is clicked
  onRowClick?: (item: any) => void;
  // Hides the table header.
  noHeader?: boolean;
  // Indicate the number of items per page
  perPage?: number;
  // Hides the pagination panel
  noPaginating?: boolean;
  // Hides the result counter.
  noResultCounter?: boolean;
  // Text of entity in result counter
  resultCounterText?: {
    singular: string;
    plural: string;
  };
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    background: theme.palette.primary.light,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  tableWrapper: {
    position: 'relative',
    minHeight: 100,
    width: '100%',
  },
  table: {
    borderCollapse: 'initial',
    borderSpacing: 0,
    paddingTop: 0,
  },
  tableBody: {
    position: 'relative',
  },
  tr: {
    backgroundColor: colors.whiteLilac,
    '&:hover': {
      backgroundColor: colors.whiteIce,
    },
  },
  trHighlight: {
    backgroundColor: colors.softPeach,
    '&:hover': {
      backgroundColor: colors.softPeachDark,
    },
  },
  pointer: {
    cursor: 'pointer',
  },
  th: {
    fontSize: 18,
    fontWeight: theme.typography.h1.fontWeight,
    fontFamily: theme.typography.h1.fontFamily,
    background: colors.whiteIce,
    '&:first-child': {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderBottomLeftRadius: theme.shape.borderRadius,
    },
    '&:last-child': {
      borderTopRightRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
    },
  },
  td: {},
  cell: {
    color: theme.palette.primary.dark,
    borderBottom: `${theme.spacing(1)}px solid white`,
    padding: `${theme.spacing(2)}px 0`,
    '&:first-child': {
      paddingLeft: theme.spacing(1),
    },
    '&:last-child': {
      paddingRight: theme.spacing(1),
    },
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      padding: `0 ${theme.spacing(1)}px`,
    },
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  filtersContent: {
    padding: theme.spacing(2),
  },
  buttonMargin: {
    marginRight: theme.spacing(2),
  },
  loader: {
    position: 'absolute',
    top: theme.spacing(2),
    zIndex: 2,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: `calc(100% - ${theme.spacing(2)}px)`,
    opacity: 1,
  },
  loaderHidden: {
    opacity: 0,
    zIndex: -1,
  },
  noResults: {
    textAlign: 'center',
    width: '100%',
    marginTop: theme.spacing(2),
    paddingBottom: theme.spacing(3),
  },
  paginationBox: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  pagination: {
    '& ul': {
      justifyContent: 'center',
      '& li:first-child': {
        marginRight: theme.spacing(4),
        '& button': {
          background: colors.primaryLight,
          paddingTop: 0,
          boxShadow: 'none',
          '&:not(.Mui-disabled)': {
            color: theme.palette.primary.contrastText,
            background: theme.palette.primary.dark,
            '&:hover': {
              background: theme.palette.primary.main,
            },
          },
        },
      },
      '& li:last-child': {
        marginLeft: theme.spacing(3),
        '& button': {
          background: colors.primaryLight,
          paddingTop: 0,
          boxShadow: 'none',
          '&:not(.Mui-disabled)': {
            color: theme.palette.primary.contrastText,
            background: theme.palette.primary.dark,
            '&:hover': {
              background: theme.palette.primary.main,
            },
          },
        },
      },
      '& li': {
        marginRight: theme.spacing(1),
        '& button': {
          height: 40,
          width: 40,
          borderRadius: '50%',
          paddingTop: 5,
          fontSize: 18,
          fontFamily: theme.typography.h1.fontFamily,
          fontWeight: theme.typography.fontWeightBold,
          boxShadow: theme.shadows[2],
          '& svg': {
            fontSize: 24,
          },
        },
        '& button.Mui-selected': {
          color: theme.palette.primary.contrastText,
          background: theme.palette.primary.dark,
          '&:hover': {
            background: theme.palette.primary.main,
          },
        },
      },
    },
  },
  goToPageBox: {
    display: 'flex',
    '& div:first-child': {
      marginRight: theme.spacing(1),
      lineHeight: '34px',
    },
  },
  goToPageInput: {
    width: '84px',
  },
  colorPrimaryDark: {
    color: theme.palette.primary.dark,
  },
  filters: {
    fontWeight: theme.typography.fontWeightLight,
  },
  searchIcon: {
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5),
  },
}));

// prevent the onRowClick near a button to avoid precise clicking
export const cancelOnRowClick = (e: React.MouseEvent<HTMLElement>) => {
  return e.stopPropagation();
};

const DataTable = (props: DataTableProps) => {
  const classes = useStyles();
  const {
    id,
    repository,
    columns,
    deletable,
    onDeleteFail,
    actions,
    deleteItemMessage,
    className,
    contained,
    persistFilters,
    defaultFilters,
    onFiltersChange,
    onLoad,
    renderRow,
    styleRow,
    onRowClick,
    resultCounterText,
  } = props;
  const { query } = useContext(SearchContext);
  const [items, setItems] = useState<Array<unknown>>([]);
  const [ready, setReady] = useState<boolean>(false);

  const triggerSearchManually = props.triggerSearchManually === true;
  const [searchLock, setSearchLock] = useState<boolean>(triggerSearchManually);
  const [loading, setLoading] = useState<boolean>(false);

  const { appState, setAppState, localStore, roleViewManager } =
    useContext(AppContext);
  const [cancelTokens, setCancelTokens] = useState<CancelTokenSource[]>([]);

  const itemsPerPage = props?.perPage || 10;
  const withDelete = deletable || false;
  const isContained = contained === undefined ? false : contained;

  const showActionsColumn = actions !== undefined || withDelete;

  const [paginator, setPaginator] = useState<{
    totalPages: number | null;
    totalItems: number | null;
    currentPage: number;
  }>({
    totalPages: null,
    totalItems: null,
    currentPage: 1,
  });
  const [goToPageNo, setGoToPageNo] = useState<string>(
    String(paginator.currentPage),
  );

  const { currentPage } = paginator;

  const initialFilters = useMemo(
    () =>
      defaultFilters
        ? { ...defaultFilters, filters: { ...defaultFilters.filters } }
        : {
            filters: {},
            order: undefined,
          },
    [defaultFilters],
  );

  const [filters, setFilters] = useState<ApiFilterCriteria>(initialFilters);

  const canClearFilters =
    props.filters &&
    props.filters.length > 0 &&
    JSON.stringify(filters) !== JSON.stringify(initialFilters);

  const [deleteState, setDeleteState] = useState<{
    showDialog: boolean;
    item: Item | null;
  }>({
    showDialog: false,
    item: null,
  });

  const resetPaginator = () =>
    setPaginator({ totalPages: null, totalItems: null, currentPage: 1 });

  const handleClearFilters = () => {
    setFilters(initialFilters);
    localStore.removeItem(`datatable-${id}-filters`);
  };

  /**
   * Data Loading
   */
  const loadItems = async (
    query: string,
    filters: ApiFilterCriteria,
    currentPage: number,
  ) => {
    cancelTokens.forEach((c) => {
      c.cancel();
    });

    const token = axios.CancelToken.source();
    setCancelTokens([...cancelTokens, token]);

    setLoading(true);

    try {
      repository
        .findBy({ ...filters, query: query || '' }, currentPage, itemsPerPage)
        .then((response) => {
          let totalItems: number;
          let items;

          if ('data' in response) {
            const { data } = response;

            if ('hydra:member' in data && 'hydra:totalItems' in data) {
              items = data['hydra:member'];
              totalItems = data['hydra:totalItems'];
            } else {
              items = data.items;
              totalItems = data.totalItems;
            }
          } else {
            items = response.items;
            totalItems = response.totalItems;
          }

          if (onLoad) {
            onLoad(items, totalItems);
          }

          const totalPages = Math.ceil(totalItems / itemsPerPage);

          setItems(items);
          setPaginator({
            currentPage: currentPage <= totalPages ? currentPage : 1,
            totalItems,
            totalPages,
          });
        })
        .catch((err) => {
          handleClearFilters();
          if (err.response && setAppState && appState) {
            setAppState({ ...appState, errorStatusCode: err.response.status });
          }
        })
        .finally(() => setLoading(false));
    } catch (e: any) {
      handleClearFilters();
      resetPaginator();
      if (e.response && setAppState && appState) {
        setAppState({ ...appState, errorStatusCode: e.response.status });
      }
    } finally {
      setCancelTokens((prev) => prev.filter((c) => c !== token));
    }
  };

  const doSearch = useCallback(
    _.debounce((query, filters, currentPage) => {
      if (!ready) {
        return;
      }

      loadItems(query, filters, currentPage);

      // lock search until we trigger the search again (default no lock)
      setSearchLock(triggerSearchManually);
    }, 500),
    [ready],
  );

  useEffect(() => {
    if (!searchLock) {
      doSearch(query, filters, currentPage);
    }
  }, [query, filters, currentPage, doSearch, searchLock]);

  /**
   * Pagination
   */
  const handlePaginate = (_e: ChangeEvent<unknown>, page: number) => {
    setPaginator((prev) => ({ ...prev, currentPage: page }));
    setSearchLock(false);
  };
  const handleGoToPageNoChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setGoToPageNo(event.target.value);
    setSearchLock(false);
  };
  const goToPage = () => {
    const intValue = parseInt(goToPageNo);
    setPaginator((prev) => ({
      ...prev,
      currentPage: !isNaN(intValue) ? intValue : 1,
    }));
    setSearchLock(false);
  };

  /**
   * Deletion
   */
  const handleDelete = (item: any) => {
    setDeleteState({ item, showDialog: true });
  };

  const handleClose = () => {
    setDeleteState({ item: null, showDialog: false });
  };

  const doDelete = () => {
    if (typeof repository.delete !== 'function') {
      return;
    }

    if (deleteState.item !== null) {
      repository
        .delete(deleteState.item.id)
        .then(() => {
          loadItems(query, filters, currentPage);
        })
        .catch(() => {
          if (onDeleteFail) {
            onDeleteFail(deleteState.item);
          }
        });
    }

    setDeleteState({ item: null, showDialog: false });
  };

  /**
   * Sorting
   */
  const handleSort = (property: string) => () => {
    let order: 'asc' | 'desc' = 'asc';

    if (filters.order && filters.order.length > 0) {
      order = filters.order[0].order === 'asc' ? 'desc' : 'asc';
    }

    resetPaginator();
    setFilters({ ...filters, order: [{ field: property, order }] });
  };

  /**
   * Filtering
   */
  const handleFilterColumn = (field: string, options: FilterColumnOption[]) => {
    const updatedFilters = { ...filters };

    if (updatedFilters.filters === undefined) {
      updatedFilters.filters = {};
    }

    if (options.length === 0) {
      delete updatedFilters.filters[field];
    } else {
      updatedFilters.filters[field] = options;
    }

    resetPaginator();
    setFilters(updatedFilters);
  };

  const handleTextFilterColumn = (field: string, value: string) => {
    const updatedFilters = { ...filters };

    if (updatedFilters.filters === undefined) {
      updatedFilters.filters = {};
    }

    if (value.length === 0) {
      delete updatedFilters.filters[field];
    } else {
      updatedFilters.filters[field] = value;
    }

    resetPaginator();
    setFilters(updatedFilters);
  };

  const handleFilterColumnByDate = (
    field: string,
    date: MaterialUiPickersDate,
  ) => {
    const updatedFilters = { ...filters };

    if (!date) {
      if (updatedFilters.filters && updatedFilters.filters[field]) {
        delete updatedFilters.filters[field];
        setFilters(updatedFilters);
      }

      return;
    }

    if (updatedFilters.filters === undefined) {
      updatedFilters.filters = {};
    }

    resetPaginator();
    updatedFilters.filters[field] = date.format('YYYY-MM-DD');
    setFilters(updatedFilters);
  };

  useEffect(
    () => {
      if (onFiltersChange) {
        onFiltersChange(filters);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  /**
   * State persistence.
   */
  useEffect(
    () => {
      if (!persistFilters || !ready) {
        return;
      }

      localStore.setItem(`datatable-${id}-filters`, JSON.stringify(filters));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters],
  );

  useEffect(
    () => {
      if (!persistFilters) {
        setReady(true);
        return;
      }

      localStore
        .getItem<string>(`datatable-${id}-filters`)
        .then((storedFiltersStr) => {
          if (storedFiltersStr === null) {
            return;
          }

          const storedFilters = JSON.parse(storedFiltersStr);
          setFilters({ ...filters, ...storedFilters });
        })
        .finally(() => setReady(true));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    if (!ready) {
      return;
    }

    setLoading(!triggerSearchManually);
  }, [ready, triggerSearchManually]);

  return (
    <Box className={className}>
      <Dialog
        open={deleteState.showDialog}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {deleteItemMessage && deleteState.item !== null
              ? deleteItemMessage(deleteState.item)
              : 'Weet je zeker dat je dit item wilt verwijderen?'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="default" autoFocus>
            Annuleren
          </Button>
          <Button onClick={doDelete} color="secondary">
            Verwijderen
          </Button>
        </DialogActions>
      </Dialog>

      <ConditionalWrapper
        condition={isContained}
        wrapper={(children) => (
          <Paper className={classes.root}>{children}</Paper>
        )}
      >
        <>
          {(!props.noResultCounter ||
            (props.filters && props.filters.length > 0)) && (
            <Box
              display="flex"
              width="100%"
              justifyContent="space-between"
              className={`${classes.colorPrimaryDark} ${classes.filters}`}
              mb={2}
              mt={1}
            >
              <Box display="flex" alignItems="center">
                {props.filters && props.filters.length >= 1 && (
                  <>
                    <Box mr={2}>Filter op:</Box>
                    {props.filters.map((filter) => {
                      if (filter?.type === 'text') {
                        return (
                          <TextFilter
                            key={filter.field}
                            field={filter.field}
                            name={filter.name}
                            onChange={(value: string) => {
                              handleTextFilterColumn(filter.field, value);
                            }}
                            criteria={filters}
                          />
                        );
                      }

                      if (filter?.type === 'checkbox') {
                        return (
                          <CheckboxFilter
                            key={filter.field}
                            field={filter.field}
                            name={filter.name}
                            options={filter.options}
                            onChange={(options: FilterColumnOption[]) => {
                              return handleFilterColumn(filter.field, options);
                            }}
                            criteria={filters}
                            infoText={filter.infoText}
                          />
                        );
                      }

                      if (filter?.type === 'datepicker') {
                        return (
                          <DateFilter
                            key={filter.field}
                            field={filter.field}
                            name={filter.name}
                            onChange={(date: MaterialUiPickersDate) => {
                              return handleFilterColumnByDate(
                                filter.field,
                                date,
                              );
                            }}
                            criteria={filters}
                          />
                        );
                      }

                      if (filter?.type === 'dateRange') {
                        return (
                          <DateRangeFilter
                            key={filter.field}
                            field={filter.field}
                            name={filter.name}
                            onMinDateChange={(date: MaterialUiPickersDate) => {
                              return handleFilterColumnByDate(
                                `${filter.field}Min`,
                                date,
                              );
                            }}
                            onMaxDateChange={(date: MaterialUiPickersDate) => {
                              return handleFilterColumnByDate(
                                `${filter.field}Max`,
                                date,
                              );
                            }}
                            criteria={filters}
                          />
                        );
                      }

                      return null;
                    })}
                  </>
                )}
                {triggerSearchManually ? (
                  <Tooltip title="Zoekresultaat bijwerken">
                    <IconButton
                      size="small"
                      onClick={() => setSearchLock(false)}
                      className={classes.searchIcon}
                    >
                      <Box position="relative">
                        <FontAwesomeIcon icon={['fal', 'search']} />
                      </Box>
                    </IconButton>
                  </Tooltip>
                ) : null}
                {canClearFilters ? (
                  <Tooltip title="Filters verwijderen">
                    <IconButton
                      size="small"
                      onClick={handleClearFilters}
                      className={classes.searchIcon}
                    >
                      <Box position="relative">
                        <FontAwesomeIcon icon={['fal', 'filter']} />
                        <FontAwesomeIcon icon="trash" size="sm" />
                      </Box>
                    </IconButton>
                  </Tooltip>
                ) : null}
              </Box>
            </Box>
          )}
          <div className={classes.tableWrapper}>
            <TableContainer>
              <Table className={classes.table}>
                {!props.noHeader && (
                  <TableHead>
                    <TableRow>
                      {columns.map((column) => (
                        <TableCell
                          key={`cell-header-${column.field}`}
                          className={`${classes.th} ${classes.cell}`}
                        >
                          <Box display="flex">
                            {column.sortable && (
                              <TableSortLabel
                                active={
                                  filters.order &&
                                  filters.order[0].field === column.field
                                }
                                direction={
                                  filters.order ? filters.order[0].order : 'asc'
                                }
                                onClick={handleSort(column.field)}
                                data-testid={`column-sort-label-${column.field}`}
                              >
                                {column.name}
                              </TableSortLabel>
                            )}
                            {!column.sortable &&
                              column.filter === undefined &&
                              column.name}
                          </Box>
                        </TableCell>
                      ))}
                      {showActionsColumn && (
                        <TableCell
                          className={`${classes.th} ${classes.cell}`}
                        />
                      )}
                    </TableRow>
                  </TableHead>
                )}
                <TableBody className={classes.tableBody}>
                  {items.map((item) => {
                    const renderedColumns = (
                      <>
                        {columns.map((column) => (
                          <TableCell
                            key={`${(item as { id: string }).id}-cell-${
                              column.field
                            }`}
                            className={`${classes.td} ${classes.cell}`}
                          >
                            <Box>
                              {column.render &&
                                column.render(item, roleViewManager)}
                              {!column.render && (item as any)[column.field]}
                            </Box>
                          </TableCell>
                        ))}
                        {showActionsColumn && (
                          <TableCell
                            key={`${(item as { id: string }).id}-cell-actions`}
                            className={`${classes.td} ${classes.cell}`}
                          >
                            <Box
                              justifyContent="flex-end"
                              onClick={onRowClick ? cancelOnRowClick : () => {}}
                            >
                              <div className={classes.actions}>
                                {actions && actions(item)}
                                {withDelete && (
                                  <Tooltip title="Verwijderen">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDelete(item as any)}
                                    >
                                      <FontAwesomeIcon
                                        icon={['fal', 'trash']}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </div>
                            </Box>
                          </TableCell>
                        )}
                      </>
                    );

                    let trStyle = classes.tr;
                    if (styleRow) {
                      trStyle = classes[styleRow(item)];
                    }
                    if (onRowClick) {
                      trStyle += ` ${classes.pointer}`;
                    }

                    if (renderRow) {
                      return renderRow(
                        renderedColumns,
                        item,
                        trStyle,
                        `${classes.td} ${classes.cell}`,
                      );
                    }

                    return (
                      <TableRow
                        key={(item as { id: string }).id}
                        data-testid="table-row"
                        className={trStyle}
                        onClick={() => (onRowClick ? onRowClick(item) : {})}
                      >
                        {renderedColumns}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {!loading && items.length === 0 && (
              <div className={classes.noResults}>
                <p>{`Geen ${
                  resultCounterText ? resultCounterText.plural : 'resultaten'
                } gevonden.`}</p>
                {triggerSearchManually ? (
                  <p>
                    <b>Let op!</b> Op deze pagina verschijnen de zoekresultaten
                    niet automatisch. Klik op het zoekicoon boven de tabel om
                    (opnieuw) te zoeken.
                  </p>
                ) : null}
              </div>
            )}
            <div
              className={`${classes.loader} ${
                loading ? '' : classes.loaderHidden
              }`}
            >
              <CircularProgress />
            </div>
          </div>
          {!props.noPaginating &&
            paginator.totalPages !== null &&
            paginator.totalPages > 1 && (
              <Box className={classes.paginationBox}>
                <Pagination
                  count={paginator.totalPages}
                  page={paginator.currentPage}
                  onChange={handlePaginate}
                  className={classes.pagination}
                />
                <Box className={classes.goToPageBox}>
                  <Box>Direct naar pagina:</Box>
                  <TextField
                    type="text"
                    className={classes.goToPageInput}
                    value={goToPageNo}
                    onChange={(event) => handleGoToPageNoChange(event)}
                    onKeyUp={(event) =>
                      event.key === 'Enter' ? goToPage() : undefined
                    }
                    onBlur={() => goToPage()}
                    size="small"
                  />
                </Box>
              </Box>
            )}
        </>
      </ConditionalWrapper>
    </Box>
  );
};

export default DataTable;
